import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Icon, Input, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useBooleanState, ErrorCode } from 'libs.react';
import { AuthWrapper } from '../../components';
import { AuthContext, AuthRoute } from '../../features';
export const SwitchWorkspacePage = () => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { user, login, mfaChallenge, redirectToDefaultPage, ssoLogin } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const passwordRef = useRef(null);
    const { id } = useParams();
    const [password, setPassword] = useState('');
    const [isPasswordMode, enablePasswordMode, disablePasswordMode] = useBooleanState(false);
    const entityIdp = location.state?.entityIdp;
    const isSSOAvailable = entityIdp?.loginMethods.includes('sso');
    const isPasswordAvailable = !entityIdp || entityIdp.loginMethods.includes('password');
    const hasMultipleIdps = entityIdp && entityIdp.idps.length > 1;
    const showPassword = isPasswordMode || (!isSSOAvailable && isPasswordAvailable);
    const showSSO = !isPasswordMode && isSSOAvailable;
    // Redirect to MFA page when mfaChallenge is set after login
    useEffect(() => {
        if (mfaChallenge) {
            navigate({
                pathname: mfaChallenge.needsEnrollment ? AuthRoute.MFA_SETUP : AuthRoute.MFA_CHALLENGE,
                search: location.search,
            });
        }
    }, [location.search, mfaChallenge, navigate]);
    const isSubmitDisabled = showPassword ? !password : false;
    const onPasswordChange = (event) => {
        setPassword(event.target.value);
    };
    const onSubmit = async () => {
        if (!isSubmitDisabled && user && id) {
            if (showPassword) {
                try {
                    const result = await login(user.email, password, Number(id));
                    if (!result.mfaRequired) {
                        await redirectToDefaultPage();
                    }
                }
                catch (err) {
                    if (err instanceof AxiosError && err.response?.data?.errCode === ErrorCode.ACCESS_DENIED_SSO_REQUIRED) {
                        addNotification({
                            title: translate('SSO required'),
                            subtitle: translate('Password login is disabled for your account. Please use SSO.'),
                            type: 'error',
                        });
                        disablePasswordMode();
                    }
                    else {
                        throw err;
                    }
                }
            }
            else if (isSSOAvailable && !hasMultipleIdps && entityIdp) {
                await ssoLogin(entityIdp, user.email);
            }
        }
    };
    return (_jsx(AuthWrapper, { onSubmit: onSubmit, title: translate('Sign in to your account'), children: _jsxs("div", { className: 'flex flex-col gap-6', children: [showPassword && (_jsxs(_Fragment, { children: [_jsx(Input, { "data-testid": 'login-password-input', id: 'password', label: translate('Password'), onChange: onPasswordChange, ref: passwordRef, required: true, hideRequiredIndicator: true, type: 'password', value: password, width: 'full' }), _jsx(Button, { dataTestId: 'switch-workspace-submit-button', label: translate('Sign in'), disabled: isSubmitDisabled })] })), showSSO && hasMultipleIdps && entityIdp && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Text, { size: 'sm', className: 'text-secondary font-medium', children: translate('Choose your identity provider') }), entityIdp.idps.map((idp) => (_jsxs("button", { type: 'button', "data-testid": `idp-option-${idp.key}`, className: 'flex items-center justify-center gap-3 w-full rounded-lg border border-neutral-300 p-2 hover:bg-neutral-50 transition-colors', onClick: () => user && ssoLogin(entityIdp, user.email, undefined, idp.key), children: [idp.logo ? (_jsx("img", { src: idp.logo, alt: '', className: 'h-6 w-6 object-contain' })) : (_jsx(Icon, { name: 'keySolid', size: 'sm', color: 'gray' })), _jsx("span", { className: 'text-sm font-medium', children: idp.displayName || idp.name })] }, idp.key)))] })), showSSO && !hasMultipleIdps && (_jsx(Button, { dataTestId: 'login-continue-button', label: translate('Sign in with SSO'), disabled: isSubmitDisabled })), isSSOAvailable && isPasswordAvailable && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex items-center max-w-[27.5rem]', children: [_jsx("div", { className: 'border-b w-full' }), _jsx(Text, { size: 'sm', className: 'mx-4 text-secondary font-medium', children: translate('or') }), _jsx("div", { className: 'border-b w-full' })] }), showPassword ? (_jsx(Button, { type: 'button', label: translate('Sign in with SSO'), onClick: disablePasswordMode, variant: 'secondary' })) : (_jsx(Button, { type: 'button', dataTestId: 'login-with-password-button', label: translate('Sign in with password'), onClick: enablePasswordMode, variant: 'secondary' }))] }))] }) }));
};
